import { get } from 'lodash';

import alertIcon from '~/assets/feed-item-type-icons/alert.svg';
import bhaIcon from '~/assets/feed-item-type-icons/bha.svg';
import dailyCostIcon from '~/assets/feed-item-type-icons/daily-cost.svg';
import documentIcon from '~/assets/feed-item-type-icons/document.svg';
import drillerMemoIcon from '~/assets/feed-item-type-icons/driller-memo.svg';
import fluidReportIcon from '~/assets/feed-item-type-icons/fluid-report.svg';
import operationalNoteIcon from '~/assets/feed-item-type-icons/operational-note.svg';
import operationSummaryIcon from '~/assets/feed-item-type-icons/operation-summary.svg';
import surveyStationIcon from '~/assets/feed-item-type-icons/survey-station.svg';
import annotationMenuIcon from '~/assets/annotations-menu.svg';
import lessonsLearnedIcon from '~/assets/feed-item-type-icons/lessons-learned.svg';
import nptIcon from '~/assets/feed-item-type-icons/npt.svg';
import { FEED_ITEM_TYPES } from '~/constants/feed';
import { convertDateStringToUnixTimestamp } from '~/utils/time';

export const AVATAR_ICON = 'AVATAR_ICON';

const FEED_TYPES_WITH_AVATAR_ICON = [
  FEED_ITEM_TYPES.POST,
  FEED_ITEM_TYPES.APP_ANNOTATION,
  FEED_ITEM_TYPES.GEOSTEERING_COMMENTS,
  FEED_ITEM_TYPES.DVD_COMMENT,
  FEED_ITEM_TYPES.HOOKLOAD_BROOMSTICK_COMMENT,
  FEED_ITEM_TYPES.PC_COMMENT,
  FEED_ITEM_TYPES.NPT_LESSONS_COMMENT,
  FEED_ITEM_TYPES.TRACES_MEMO,
  FEED_ITEM_TYPES.DEPTH_COMMENTS,
];

export function isAvatarIcon(feedItemType) {
  return FEED_TYPES_WITH_AVATAR_ICON.includes(feedItemType);
}

export function getIconForFeedItemType(feedItemType) {
  if (isAvatarIcon(feedItemType)) {
    return AVATAR_ICON;
  } else {
    switch (feedItemType) {
      case FEED_ITEM_TYPES.ALERT:
        return alertIcon;
      case FEED_ITEM_TYPES.OPERATION_SUMMARY:
      case FEED_ITEM_TYPES.COMPLETION_OPERATION_SUMMARY:
        return operationSummaryIcon;
      case FEED_ITEM_TYPES.OPERATIONAL_NOTE:
      case FEED_ITEM_TYPES.COMPLETION_OPERATIONAL_NOTE:
        return operationalNoteIcon;
      case FEED_ITEM_TYPES.BHA:
        return bhaIcon;
      case FEED_ITEM_TYPES.DAILY_COST:
      case FEED_ITEM_TYPES.COMPLETION_DAILY_COST:
        return dailyCostIcon;
      case FEED_ITEM_TYPES.DOCUMENT:
        return documentIcon;
      case FEED_ITEM_TYPES.DRILLER_MEMO:
        return drillerMemoIcon;
      case FEED_ITEM_TYPES.FLUID_REPORT:
        return fluidReportIcon;
      case FEED_ITEM_TYPES.SURVEY_STATION:
        return surveyStationIcon;
      case FEED_ITEM_TYPES.LESSONS_LEARNED:
        return lessonsLearnedIcon;
      case FEED_ITEM_TYPES.NPT_EVENT:
      case FEED_ITEM_TYPES.COMPLETION_NPT_EVENT:
        return nptIcon;
      // FIXME: Implement when icons will be available
      // get(FEED_ITEM_TYPES_BY_KEY, 'completion_stage.type')
      // get(FEED_ITEM_TYPES_BY_KEY, 'completion_time_log.type')
      default:
        return annotationMenuIcon;
    }
  }
}

/**
 * The value is used to position the FeedItem
 *
 * @returns number|null
 */
export function getFeedDepth(feedItem, isTvd) {
  const feedItemData =
    get(feedItem, ['context', feedItem.type, 'data']) || get(feedItem, ['context', feedItem.type]);
  switch (feedItem.type) {
    case FEED_ITEM_TYPES.DEPTH_COMMENTS:
      return feedItemData?.commentDepth;
    case FEED_ITEM_TYPES.HOOKLOAD_BROOMSTICK_COMMENT:
      return feedItemData?.measuredDepth;
    case FEED_ITEM_TYPES.BHA:
      return isTvd ? feedItemData?.tvd_start : feedItemData?.start_depth;
    case FEED_ITEM_TYPES.FLUID_REPORT:
      return feedItemData?.depth;
    case FEED_ITEM_TYPES.SURVEY_STATION:
      return isTvd ? feedItemData?.tvd : feedItemData?.measured_depth;
    case FEED_ITEM_TYPES.NPT_EVENT:
    case FEED_ITEM_TYPES.COMPLETION_NPT_EVENT:
      return isTvd ? feedItemData?.tvd_start : feedItemData?.depth;
    case FEED_ITEM_TYPES.NPT_LESSONS_COMMENT:
    case FEED_ITEM_TYPES.PC_COMMENT:
    case FEED_ITEM_TYPES.DRILLING_DYSFUNCTION_EVENT:
    case FEED_ITEM_TYPES.TRACES_MEMO:
    case FEED_ITEM_TYPES.DVD_COMMENT:
      return feedItemData?.holeDepth;
    case FEED_ITEM_TYPES.LESSONS_LEARNED:
      return isTvd ? feedItemData?.tvd_start : feedItemData?.md_start;
    case FEED_ITEM_TYPES.GEOSTEERING_COMMENTS:
      return feedItemData?.axis?.MD;
    case FEED_ITEM_TYPES.ALERT:
      return feedItemData?.start_hole_depth ?? null;
    case FEED_ITEM_TYPES.WELL_PLAN:
    case FEED_ITEM_TYPES.APP_ANNOTATION:
    case FEED_ITEM_TYPES.DRILLER_MEMO:
    case FEED_ITEM_TYPES.DOCUMENT:
    case FEED_ITEM_TYPES.COMPLETION_DOCUMENT:
    case FEED_ITEM_TYPES.OPERATION_SUMMARY:
    case FEED_ITEM_TYPES.OPERATIONAL_NOTE:
    case FEED_ITEM_TYPES.COMPLETION_OPERATIONAL_NOTE:
    case FEED_ITEM_TYPES.POST:
    case FEED_ITEM_TYPES.DAILY_COST:
    case FEED_ITEM_TYPES.COMPLETION_DAILY_COST:
      return null;
    default:
      return null;
  }
}

/**
 * The value is used to position the FeedItem
 *
 * @returns number|null
 */
export function getFeedItemTimestamp(feedItem) {
  const feedItemContext = get(feedItem, ['context', feedItem.type]);
  const feedItemContextData = get(feedItem, ['context', feedItem.type, 'data']);
  const feedItemData = feedItemContextData || feedItemContext;

  switch (feedItem.type) {
    case FEED_ITEM_TYPES.DEPTH_COMMENTS:
      return feedItemData?.recordTimestamp;
    case FEED_ITEM_TYPES.HOOKLOAD_BROOMSTICK_COMMENT:
      return feedItemData?.timestamp;
    case FEED_ITEM_TYPES.BHA:
      return feedItemData?.start_timestamp;
    case FEED_ITEM_TYPES.FLUID_REPORT:
    case FEED_ITEM_TYPES.DAILY_COST:
      return feedItemData?.date;
    case FEED_ITEM_TYPES.SURVEY_STATION:
    case FEED_ITEM_TYPES.POST:
    case FEED_ITEM_TYPES.GEOSTEERING_COMMENTS:
      return feedItem?.created_at ? convertDateStringToUnixTimestamp(feedItem.created_at) : null;
    case FEED_ITEM_TYPES.NPT_EVENT:
    case FEED_ITEM_TYPES.COMPLETION_NPT_EVENT:
      return feedItemData?.start_time;
    case FEED_ITEM_TYPES.NPT_LESSONS_COMMENT:
    case FEED_ITEM_TYPES.PC_COMMENT:
    case FEED_ITEM_TYPES.DRILLING_DYSFUNCTION_EVENT:
    case FEED_ITEM_TYPES.TRACES_MEMO:
    case FEED_ITEM_TYPES.DVD_COMMENT:
      return feedItemData?.timestamp;
    case FEED_ITEM_TYPES.LESSONS_LEARNED:
      return feedItemData?.start_time;
    case FEED_ITEM_TYPES.OPERATION_SUMMARY:
    case FEED_ITEM_TYPES.OPERATIONAL_NOTE:
      return feedItemData?.date_time;
    case FEED_ITEM_TYPES.ALERT:
      return feedItemContext?.alert_at
        ? convertDateStringToUnixTimestamp(feedItemContext.alert_at)
        : null;

    case FEED_ITEM_TYPES.WELL_PLAN:
    case FEED_ITEM_TYPES.APP_ANNOTATION:
    case FEED_ITEM_TYPES.DRILLER_MEMO:
    case FEED_ITEM_TYPES.DOCUMENT:
    case FEED_ITEM_TYPES.COMPLETION_DOCUMENT:
    case FEED_ITEM_TYPES.COMPLETION_OPERATIONAL_NOTE:
    case FEED_ITEM_TYPES.COMPLETION_DAILY_COST:
      return null;
    default:
      return null;
  }
}
